import { isMassDeleteBatch } from './incomingMergeUtils'
import { parseSyncManifest } from './tuneShardSync'

export const LAST_DRIVE_UPLOAD_STORAGE_KEY = 'bookstorage_last_drive_upload'
export const DRIVE_UPLOAD_ECHO_PAUSE_MS = 10000

const SAMPLE_LIMIT = 12

function tuneDisplayName(tune, id) {
  if (!tune) return id || '(untitled)'
  const name = String(tune.name || tune.title || '').trim()
  return name || id || '(untitled)'
}

function toMs(ts) {
  return parseInt(ts, 10) || 0
}

function mapMsById(records, tsKey) {
  const out = {}
  Object.keys(records || {}).forEach(function(id) {
    const rec = records[id]
    if (!rec) return
    const ms = toMs(rec[tsKey])
    if (ms > 0) out[String(id)] = ms
  })
  return out
}

function readMsMap(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out = {}
  Object.keys(raw).forEach(function(id) {
    const ms = toMs(raw[id])
    if (ms > 0) out[String(id)] = ms
  })
  return out
}

/** Stable numeric hash of ABC text for identical-echo skips. */
export function hashDriveAbc(abcText) {
  const str = abcText == null ? '' : String(abcText)
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return String(4294967296 * (2097151 & h2) + (h1 >>> 0))
}

export function isLastDriveUploadAbcEcho(abcText) {
  const snap = readLastDriveUploadSnapshot()
  if (!snap || !snap.abcHash) return false
  return snap.abcHash === hashDriveAbc(abcText)
}

/**
 * Pause Drive change polling around an upload, then resume after the echo window.
 * `pauseRef` is the same ref passed to useGoogleDocument as pausePolling.
 */
export function createDrivePollPauseController(pauseRef, delayMs) {
  var timer = null
  var delay = typeof delayMs === 'number' ? delayMs : DRIVE_UPLOAD_ECHO_PAUSE_MS
  return {
    pause: function() {
      if (pauseRef) pauseRef.current = true
    },
    resumeNow: function() {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      if (pauseRef) pauseRef.current = false
    },
    resumeAfterEcho: function() {
      if (timer) clearTimeout(timer)
      timer = setTimeout(function() {
        timer = null
        if (pauseRef) pauseRef.current = false
      }, delay)
    },
  }
}

export function readLastDriveUploadSnapshot() {
  try {
    const raw = localStorage.getItem(LAST_DRIVE_UPLOAD_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const ids = Array.isArray(parsed.ids) ? parsed.ids.map(String) : []
    const names = parsed.names && typeof parsed.names === 'object' ? parsed.names : {}
    return {
      count: typeof parsed.count === 'number' ? parsed.count : ids.length,
      ids: ids,
      names: names,
      savedAt: parsed.savedAt || null,
      abcHash: parsed.abcHash ? String(parsed.abcHash) : null,
      lastUpdatedById: readMsMap(parsed.lastUpdatedById),
      deletedAtById: readMsMap(parsed.deletedAtById),
      playlistUpdatedAtById: readMsMap(parsed.playlistUpdatedAtById),
      playlistDeletedAtById: readMsMap(parsed.playlistDeletedAtById),
      setUpdatedAtById: readMsMap(parsed.setUpdatedAtById),
      setDeletedAtById: readMsMap(parsed.setDeletedAtById),
      practiceListUpdatedAtById: readMsMap(parsed.practiceListUpdatedAtById),
      practiceListDeletedAtById: readMsMap(parsed.practiceListDeletedAtById),
    }
  } catch (e) {
    return null
  }
}

export function writeLastDriveUploadSnapshot(tunes, extras) {
  const extra = extras || {}
  const map = tunes || {}
  const ids = Object.keys(map)
  const names = {}
  ids.forEach(function(id) {
    names[id] = tuneDisplayName(map[id], id)
  })
  const snapshot = {
    count: ids.length,
    ids: ids,
    names: names,
    savedAt: Date.now(),
    lastUpdatedById: mapMsById(map, 'lastUpdated'),
    deletedAtById: mapMsById(extra.deletedTunes, 'deletedAt'),
    playlistUpdatedAtById: mapMsById(extra.playlists, 'updatedAt'),
    playlistDeletedAtById: mapMsById(extra.deletedPlaylists, 'deletedAt'),
    setUpdatedAtById: mapMsById(extra.performanceSets, 'updatedAt'),
    setDeletedAtById: mapMsById(extra.deletedPerformanceSets, 'deletedAt'),
    practiceListUpdatedAtById: mapMsById(extra.practiceLists, 'updatedAt'),
    practiceListDeletedAtById: mapMsById(extra.deletedPracticeLists, 'deletedAt'),
  }
  if (extra.abcHash != null && extra.abcHash !== '') {
    snapshot.abcHash = String(extra.abcHash)
  } else if (typeof extra.abc === 'string') {
    snapshot.abcHash = hashDriveAbc(extra.abc)
  }
  try {
    localStorage.setItem(LAST_DRIVE_UPLOAD_STORAGE_KEY, JSON.stringify(snapshot))
  } catch (e) {
    // ignore quota / private mode
  }
  clearDismissedDriveUploadShrink()
  return snapshot
}

/**
 * Build a shrink warning when the pending upload would remove a dangerous
 * number of tunes vs the last successful Drive upload.
 * Returns null when no confirmation is needed.
 */
export function buildDriveUploadShrinkWarning(previousSnapshot, nextTunes) {
  const prev = previousSnapshot || null
  const next = nextTunes || {}
  if (!prev || !Array.isArray(prev.ids) || prev.ids.length === 0) return null

  const nextIds = Object.keys(next)
  const nextSet = {}
  nextIds.forEach(function(id) { nextSet[id] = true })

  const removedIds = prev.ids.filter(function(id) { return !nextSet[id] })
  const removedCount = removedIds.length
  if (!isMassDeleteBatch(removedCount, prev.count || prev.ids.length)) return null

  const prevNames = prev.names || {}
  const sampleNames = removedIds.slice(0, SAMPLE_LIMIT).map(function(id) {
    return prevNames[id] || id
  })

  return {
    previousCount: prev.count || prev.ids.length,
    nextCount: nextIds.length,
    removedCount: removedCount,
    addedCount: nextIds.filter(function(id) {
      return prev.ids.indexOf(id) === -1
    }).length,
    sampleNames: sampleNames,
    sampleTruncated: removedIds.length > SAMPLE_LIMIT,
  }
}

export function shouldConfirmDriveUploadShrink(previousSnapshot, nextTunes) {
  return !!buildDriveUploadShrinkWarning(previousSnapshot, nextTunes)
}

/**
 * Count tunes in a Drive songbook ABC. Prefer shard manifest totals when present
 * (X: line counts alone miss the point of sharded libraries).
 */
export function countTunesInDriveAbc(abcText) {
  const manifest = parseSyncManifest(abcText)
  if (manifest && Array.isArray(manifest.shards) && manifest.shards.length > 0) {
    let total = 0
    let any = false
    manifest.shards.forEach(function(shard) {
      const n = shard && shard.tuneCount != null ? Number(shard.tuneCount) : NaN
      if (Number.isFinite(n) && n >= 0) {
        total += n
        any = true
      }
    })
    if (any) return total
  }
  return (String(abcText || '').match(/^X:/gm) || []).length
}

/**
 * True when uploading local over the live Drive head would wipe a dangerous
 * number of remote-only tunes. Unlike buildDriveUploadShrinkWarning, this uses
 * the actual Drive document — not this device's last-upload snapshot.
 *
 * That gap is what let a tablet with a fresh 1150-tune snapshot overwrite a
 * browser-synced ~3300-tune Drive file: local vs last-local looked fine.
 */
export function buildDriveLiveOverwriteWarning(remoteTuneCount, localTuneCount) {
  const remote = remoteTuneCount > 0 ? remoteTuneCount : 0
  const local = localTuneCount > 0 ? localTuneCount : 0
  if (remote <= local) return null
  const removedCount = remote - local
  if (!isMassDeleteBatch(removedCount, remote)) return null
  return {
    previousCount: remote,
    nextCount: local,
    removedCount: removedCount,
    addedCount: 0,
    liveRemote: true,
    sampleNames: [],
    sampleTruncated: false,
  }
}

export function shouldPullBeforeDriveUpload(remoteTuneCount, localTuneCount) {
  const remote = remoteTuneCount > 0 ? remoteTuneCount : 0
  const local = localTuneCount > 0 ? localTuneCount : 0
  return remote > local
}

/** Session-only: user cancelled uploading this massively-shrunk local book. */
let dismissedShrinkKey = null

function shrinkWarningKey(warning) {
  if (!warning) return null
  return String(warning.previousCount) + '>' + String(warning.nextCount) + ':' + String(warning.removedCount)
}

export function rememberDismissedDriveUploadShrink(warning) {
  dismissedShrinkKey = shrinkWarningKey(warning)
  return dismissedShrinkKey
}

export function clearDismissedDriveUploadShrink() {
  dismissedShrinkKey = null
}

export function isDismissedDriveUploadShrink(warning) {
  const key = shrinkWarningKey(warning)
  return !!(key && dismissedShrinkKey && key === dismissedShrinkKey)
}

export function resetDismissedDriveUploadShrinkForTests() {
  dismissedShrinkKey = null
}

/**
 * Count tunes present in the last successful Drive upload snapshot but missing
 * from the current local map (memory or IndexedDB).
 */
export function countMissingFromLastUpload(localTunes, previousSnapshot) {
  const snap = previousSnapshot || null
  if (!snap) return 0
  const ids = Array.isArray(snap.ids) && snap.ids.length > 0
    ? snap.ids
    : Object.keys(snap.lastUpdatedById || {})
  const local = localTunes || {}
  let missing = 0
  ids.forEach(function(id) {
    if (!local[String(id)]) missing += 1
  })
  return missing
}

/**
 * True when local looks accidentally wiped vs the last Drive upload we made
 * from this browser (mass-missing tunes). Used to re-offer Drive inserts
 * instead of treating those tunes as intentional local deletes / upload echoes.
 */
export function isLocalLibraryWipeVsLastUpload(localTunes, previousSnapshot) {
  const snap = previousSnapshot || null
  if (!snap) return false
  const ids = Array.isArray(snap.ids) && snap.ids.length > 0
    ? snap.ids
    : Object.keys(snap.lastUpdatedById || {})
  const baseCount = typeof snap.count === 'number' ? snap.count : ids.length
  if (baseCount <= 0) return false
  return isMassDeleteBatch(countMissingFromLastUpload(localTunes, snap), baseCount)
}
